import { ErrorsEnum } from 'src/app/core/services/error.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { IReport } from '../../../models/Report';
import * as moment from 'moment/moment';
import { MatDialog } from '@angular/material/dialog';
import { ReportDetailsModalComponent } from '../../modals/report-details-modal/report-details-modal.component';
import { ActionService, InventoryReviewAction, InventoryTypesEnum } from '../../core/services/action.service';
import { ActionsEnum } from '../../core/data-sources/action-data-source.service';
import { ErrorService } from '../../core/services/error.service';
import { ReportsFilterParams, ReportsService } from 'src/app/core/services/reports.service';
import { StockSortParams } from '../../core/services/stock.service';
import { Subscription } from 'rxjs';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { TaskRequestSidenavService } from 'src/app/core/services/task-request-sidenav.service';
import { Crate } from 'src/models/Crate';
import { DEFAULT_DATE_FORMAT } from 'src/app/helpers/date-format.helper';

interface DialogResponse {
  state?: 'CANCELED' | 'REJECTED' | 'APPROVED';
  inventoryId?: string;
  tagsRfidToRemove?: string[];
  openPalletInventorySidenav?: boolean;
  openRelocateSidenav?: boolean;
  crate?: Crate;
  position?: unknown;
  inventoryType?: InventoryTypesEnum;
}

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: DEFAULT_DATE_FORMAT }]
})
export class ReportsComponent implements OnInit, OnDestroy {
  reports: IReport[];
  actualHour = this.getCurrentHour();
  isLoading = false;
  appliedFilters = {};
  filterParams: ReportsFilterParams;
  sortParams: StockSortParams = {
    orderBy: 'createdAt',
    sortBy: 'DESC'
  };
  itemsSubscription: Subscription;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private actionService: ActionService,
    private errorService: ErrorService,
    public reportService: ReportsService,
    private sidenavService: TaskRequestSidenavService
  ) { }

  ngOnDestroy(): void {
    this.itemsSubscription?.unsubscribe();
  }

  ngOnInit(): void {
    this.itemsSubscription = this.reportService.items$.subscribe((items) => {
      this.reports = items;
      this.isLoading = false;
    });

    this.loadInitialReports();
  }

  async getReports(): Promise<void> {
    try {
      this.isLoading = true;
      await this.reportService.reload(this.filterParams, this.sortParams);
    } catch (e) {
      this.errorService.openErrorSnackBar(e);
    } finally {
      this.isLoading = false;
    }
  }

  itemClicked(report: IReport) {
    this.dialog.open(ReportDetailsModalComponent, {
      panelClass: 'details-modal',
      data: report,
      autoFocus: false,
      width: '87vw',
      maxWidth: '95vw'
    }).afterClosed().subscribe((res: DialogResponse) => this.handleDialogClose(res));
  }

  async handlePigeonholeInventory(res: DialogResponse) {
    switch (res.state) {
      case 'CANCELED':
        return;
      case 'APPROVED':
        await this.processPigeonholeInventoryAction(() => this.actionService.approvePigeonholeInventory({
          inventoryReportId: res.inventoryId,
          tagsRfidForExit: res.tagsRfidToRemove ?? []
        }));
        break;
      case 'REJECTED':
        await this.processPigeonholeInventoryAction(() => this.actionService.rejectPigeonholeInventory({
          inventoryReportId: res.inventoryId
        }));
        break;
    }
  }

  handlePigeonholeInventoryError(e: any) {
    switch (e?.error?.body?.type ?? e?.error?.type) {
      case ErrorsEnum.INVENTORY_REPORT_ERROR_APPROVAL_NOT_ALLOWED:
        this.errorService.openErrorSnackBarWithCustomMessage('Você não possui permissão para processar a saída da tag RFID.');
        break;
      case ErrorsEnum.INVENTORY_REPORT_ERROR_INVALID_TAG_RFID:
        this.errorService.openErrorSnackBarWithCustomMessage('Apenas pallets perdidos podem ser removidos na saída.');
        break;
      case ErrorsEnum.INVENTORY_REPORT_ERROR_TAG_RFID_NOT_FOUND:
        this.errorService.openErrorSnackBarWithCustomMessage(this.getInventoryReportTagNotFoundMessage(e));
        break;
      default:
        this.errorService.openErrorSnackBar(e);
        break;
    }
  }

  handleInventoryReviewError(e: any) {
    switch (e?.error?.body?.type ?? e?.error?.type) {
      case ErrorsEnum.INVENTORY_ERROR_NOT_FOUND:
        this.errorService.openErrorSnackBarWithCustomMessage('Inventário não encontrado.');
        break;
      case ErrorsEnum.INVENTORY_ERROR_APPROVE_INVENTORY_NOT_ALLOWED:
        this.errorService.openErrorSnackBarWithCustomMessage('Você não possui permissão para remover pallets na aprovação do inventário.');
        break;
      case ErrorsEnum.INVENTORY_ERROR_DATA_VALIDATION:
        this.errorService.openErrorSnackBarWithCustomMessage('Apenas pallets perdidos podem ser removidos.');
        break;
      case ErrorsEnum.PALLET_NOT_FOUND:
        this.errorService.openErrorSnackBarWithCustomMessage('O pallet não foi encontrado.');
        break;
      default:
        this.errorService.openErrorSnackBar(e);
        break;
    }
  }

  async handleInventoryReview(res: DialogResponse) {
    const data: InventoryReviewAction = {
      type: ActionsEnum.APPROVE_INVENTORY,
      data: { inventoryId: res.inventoryId }
    };

    switch (res.state) {
      case 'CANCELED':
        return;
      case 'APPROVED':
        data.type = ActionsEnum.APPROVE_INVENTORY;
        if (res?.tagsRfidToRemove?.length) {
          data.data.inventoryData = { tagsRfidToRemove: res.tagsRfidToRemove };
        }
        break;
      case 'REJECTED':
        data.type = ActionsEnum.REJECT_INVENTORY;
        break;
    }

    try {
      await this.actionService.inventoryReview(data);
      await this.update();
    } catch (e) {
      this.handleInventoryReviewError(e);
    }
  }

  openRelocateSidenav(res: DialogResponse) {
    const crate = res.crate;
    const productName = crate?.skuGroups?.[0]?.name ?? 'nome não disponível';
    const data = {
      type: 'STOCK_RELOCATE_SINGLE_PALLET',
      rfId: crate?.tagRfid,
      product: productName
    };
    this.sidenavService.toggle(data);
  }

  openPalletInventorySidenav(res: DialogResponse) {
    const data = {
      type: 'PALLET_INVENTORY',
      data: {
        crate: res.crate,
        position: res.position
      }
    };
    this.sidenavService.toggle(data);
  }

  toStock() {
    this.router.navigate(['stock']);
  }

  async update() {
    await this.getReports();
    this.actualHour = this.getCurrentHour();
  }

  onFilterValuesChange(params: { sortParams: StockSortParams, filterParams: ReportsFilterParams }) {
    this.reportService.currentPage = 1;
    this.filterParams = params?.filterParams;
    this.sortParams = this.setSortParams(params?.sortParams);
    this.update();
  }

  setSortParams(sort: StockSortParams) {
    if (!sort || !sort.sortBy) {
      return;
    }
    return sort;
  }

  onNextPage(event) {
    if (event) this.reportService.getNextPage(this.filterParams, this.sortParams);
  }

  onPreviousPage(event) {
    if (event) this.reportService.getPreviousPage(this.filterParams, this.sortParams);
  }

  private async loadInitialReports() {
    try {
      this.isLoading = true;
      await this.reportService.init(this.filterParams, this.sortParams);
    } catch (e) {
      this.errorService.openErrorSnackBar(e);
      this.isLoading = false;
    }
  }

  private handleDialogClose(res: DialogResponse) {
    if (!res) {
      return;
    }

    if (res.inventoryType === InventoryTypesEnum.INVENTORY_BY_PIGEONHOLE_POSITION) {
      this.handlePigeonholeInventory(res);
      return;
    }

    if (res.inventoryId) {
      this.handleInventoryReview(res);
      return;
    }

    if (res.openRelocateSidenav) {
      this.openRelocateSidenav(res);
      return;
    }

    if (res.openPalletInventorySidenav) {
      this.openPalletInventorySidenav(res);
    }
  }

  private async processPigeonholeInventoryAction(action: () => Promise<unknown>) {
    try {
      await action();
    } catch (e) {
      this.handlePigeonholeInventoryError(e);
    } finally {
      await this.update();
    }
  }

  private getCurrentHour() {
    return moment().format('HH:mm');
  }

  private getInventoryReportTagNotFoundMessage(error: any) {
    const tagsRfid = error?.error?.details?.tagsRfid ?? error?.error?.body?.details?.tagsRfid ?? [];
    const formattedTags = tagsRfid
      .filter((tagRfid: string) => !!tagRfid)
      .map((tagRfid: string) => `...${tagRfid.slice(-4)}`)
      .join(', ');

    if (!formattedTags) {
      return 'Pallets não foram encontrados no armazém.\nRejeite o relatório e realize um novo inventário.';
    }

    return `Pallets (rfid: ${formattedTags}) não foram encontrados no armazém.\nRejeite o relatório e realize um novo inventário.`;
  }
}
